// Auth token service — single source of truth for where/how the JWT lives.
//
// Today: token is stored in AppStorage (localStorage on web, native secure
// storage on mobile via cuf-expo-bridge) and sent on every request as
// `Authorization: Bearer <token>` via axios defaults.
//
// Planned (Phase C of token-storage hardening): swap this module's internals
// to rely on an httpOnly cookie set by the backend. Call sites don't change.
// Mobile app coordination required before flipping — see docs/security/
// token-storage.md (to be added alongside Phase B backend PR).
//
// Keep call sites calling the service; never touch AppStorage.set('token', ...)
// or Vue.axios.defaults.headers.common.Authorization directly from components.

import { AppStorage } from 'cuf-expo-bridge'
import axios from '@/lib/vue-axios/axios'

const TOKEN_KEY = 'token'

/**
 * Save a freshly-issued token: persist and wire it into future requests.
 * Callers: Login, signUp/setPassword, resetPasswordForm, verifyResetToken,
 * termsModal (anywhere the backend returns a token).
 */
export async function saveToken (token) {
  await AppStorage.set(TOKEN_KEY, token)
  setAuthorizationHeader(token)
}

/**
 * Clear the token: wipe storage and remove the Authorization header.
 * Callers: logout handlers in App.vue, axios 401/403 interceptor,
 * login-failure branches, terms-rejection flow.
 */
export async function clearToken () {
  await AppStorage.remove(TOKEN_KEY)
  clearAuthorizationHeader()
}

/**
 * Read the stored token on app startup. Used by main.js to restore the
 * session before mounting the Vue root.
 */
export async function loadStoredToken () {
  return AppStorage.get(TOKEN_KEY)
}

/**
 * Install token on the axios default headers. Exposed so the auth Vuex
 * action (which runs after store rehydrate) can ensure the header is set
 * without re-reading from storage.
 */
export function setAuthorizationHeader (token) {
  axios.defaults.headers.common.Authorization = 'Bearer ' + token
}

export function clearAuthorizationHeader () {
  delete axios.defaults.headers.common.Authorization
}
